<template>
  <!-- Fixed Header Menu -->
  <header :class="['fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4', isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-3']">
    <div class="max-w-6xl mx-auto">
      <div class="flex justify-between items-center">
        <!-- Logo -->
        <router-link to="/"><img :src="require('@/assets/logo.png')" class="h-[40px]" /></router-link>
        
        <!-- Desktop Menu -->
        <nav class="hidden md:flex space-x-8">
          <router-link to="/" class="text-gray-700 hover:text-red-600 font-medium">Home</router-link>
          <router-link to="/about" class="text-gray-700 hover:text-red-600 font-medium">About</router-link>
          
          <!-- solutions Dropdown -->
          <div class="relative dropdown-trigger" @mouseenter="opensolutionsDropdown" @mouseleave="closesolutionsDropdown">
            <button class="text-gray-700 hover:text-red-600 font-medium flex items-center focus:outline-none">
              Solutions
              <svg class="w-4 h-4 ml-1 transition-transform duration-200" :class="{'rotate-180': solutionsDropdownOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Dropdown Menu -->
            <div v-show="solutionsDropdownOpen" @mouseenter="opensolutionsDropdown" @mouseleave="closesolutionsDropdown" class="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-20 border border-gray-100">
              <template v-for="solution in data.solutions.list" :key="'sol_'+solution.id">
                <a
                  :href="'/solutions/'+solution.title.replace(/\s+/g, '-').toLowerCase()"
                  class="block px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                  @click="closesolutionsDropdown">
                  {{ solution.title }}
                </a>
              </template>
            </div>
          </div>
          
          <!-- Services Dropdown -->
          <div class="relative dropdown-trigger" @mouseenter="openServicesDropdown" @mouseleave="closeServicesDropdown">
            <button class="text-gray-700 hover:text-red-600 font-medium flex items-center focus:outline-none">
              Services
              <svg class="w-4 h-4 ml-1 transition-transform duration-200" :class="{'rotate-180': servicesDropdownOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Dropdown Menu -->
            <div v-show="servicesDropdownOpen" @mouseenter="openServicesDropdown" @mouseleave="closeServicesDropdown" class="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-20 border border-gray-100">
              <template v-for="service in data.services.list" :key="'menu_'+service.id">
                <a
                  :href="'/services/'+service.title.replace(/\s+/g, '-').toLowerCase()"
                  class="block px-4 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                  @click="closeServicesDropdown">
                  {{ service.title }}
                </a>
              </template>
            </div>
          </div>
          
          <router-link to="/contact" class="text-gray-700 hover:text-red-600 font-medium">Contact</router-link>
        </nav>
        
        <!-- Mobile Menu Button -->
        <button @click="toggleMobileMenu" class="md:hidden text-gray-700">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <!-- Mobile Menu -->
      <div v-if="mobileMenuOpen" class="md:hidden mt-4 pb-4">
        <div class="flex flex-col space-y-3">
          <router-link to="/" class="text-gray-700 hover:text-red-600 font-medium py-2" @click="closeMobileMenu">Home</router-link>
          <router-link to="/about" class="text-gray-700 hover:text-red-600 font-medium py-2" @click="closeMobileMenu">About</router-link>
          
          <!-- Mobile solutions Dropdown -->
          <div class="pt-2">
            <button 
              @click="togglesolutionsMobile" 
              class="flex justify-between items-center w-full text-gray-700 hover:text-red-600 font-medium py-2">
              <span>Solutions</span>
              <svg class="w-4 h-4 transition-transform duration-200" :class="{'rotate-180': solutionsMobileOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Mobile Dropdown Content -->
            <div v-if="solutionsMobileOpen" class="ml-4 space-y-2 border-l border-gray-200 pl-4 py-2">
                <template v-for="solution in data.solutions.list" :key="'sol2_'+solution.id">
                <a
                  :href="'/solutions/'+solution.title.replace(/\s+/g, '-').toLowerCase()"
                  class="block text-gray-600 hover:text-red-600 font-medium py-1"
                  @click="closeMobileMenu">
                  {{ solution.title }}
                </a>
              </template>
            </div>
          </div>
          
          <!-- Mobile Services Dropdown -->
          <div class="pt-2">
            <button 
              @click="toggleServicesMobile" 
              class="flex justify-between items-center w-full text-gray-700 hover:text-red-600 font-medium py-2">
              <span>Services</span>
              <svg class="w-4 h-4 transition-transform duration-200" :class="{'rotate-180': servicesMobileOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Mobile Dropdown Content -->
            <div v-if="servicesMobileOpen" class="ml-4 space-y-2 border-l border-gray-200 pl-4 py-2">
                <template v-for="service in data.services.list" :key="'menu2_'+service.id">
                <a
                  :href="'/services/'+service.title.replace(/\s+/g, '-').toLowerCase()"
                  class="block text-gray-600 hover:text-red-600 font-medium py-1"
                  @click="closeMobileMenu">
                  {{ service.title }}
                </a>
              </template>
            </div>
          </div>
          
          <router-link to="/contact" class="text-gray-700 hover:text-red-600 font-medium py-2" @click="closeMobileMenu">Contact</router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import homeData from '@/data/home.json'
export default {
  name: 'HomePage',
  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      servicesDropdownOpen: false,
      servicesMobileOpen: false,
      solutionsDropdownOpen: false,
      solutionsMobileOpen: false,
      dropdownCloseTimer: null,
      data: homeData
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.dropdownCloseTimer) {
      clearTimeout(this.dropdownCloseTimer)
    }
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50
      this.mobileMenuOpen = false
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
      if (this.mobileMenuOpen) {
        this.servicesMobileOpen = false
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
      this.servicesMobileOpen = false
    },
    openServicesDropdown() {
      if (this.dropdownCloseTimer) {
        clearTimeout(this.dropdownCloseTimer)
        this.dropdownCloseTimer = null
      }
      // closing other dropdown when this one opens
      this.solutionsDropdownOpen = false
      this.servicesDropdownOpen = true
    },
    closeServicesDropdown() {
      // Add a small delay to prevent dropdown from closing when moving cursor to dropdown
      this.dropdownCloseTimer = setTimeout(() => {
        this.servicesDropdownOpen = false
      }, 150)
    },
    toggleServicesMobile() {
      this.servicesMobileOpen = !this.servicesMobileOpen
    },
    opensolutionsDropdown() {
      if (this.dropdownCloseTimer) {
        clearTimeout(this.dropdownCloseTimer)
        this.dropdownCloseTimer = null
      }
      // closing other dropdown when this one opens
      this.servicesDropdownOpen = false
      this.solutionsDropdownOpen = true
    },
    closesolutionsDropdown() {
      // Add a small delay to prevent dropdown from closing when moving cursor to dropdown
      this.dropdownCloseTimer = setTimeout(() => {
        this.solutionsDropdownOpen = false
      }, 150)
    },
    togglesolutionsMobile() {
      this.solutionsMobileOpen = !this.solutionsMobileOpen
    }
  }
}
</script>

<style scoped>
/* Custom transition for dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* we manage visibility through Vue state and mouse events, so the generic ``group``/``group2``
   hover rules were unnecessary and caused crossover behaviors when one dropdown was
   hovered. keeping the handlers on the dropdown elements themselves prevents a situation
   where hovering the solutions trigger would immediately hide the services menu (and
   vice‑versa). */
</style>